import {useState, useEffect} from "react";
import {getStoryblokApi} from "@storyblok/react";
import moment from "moment";

import {
  ArticleStoryToObject,
  FilterStoriesPastPublishOffDate,
} from "../helpers/storyHelpers";
import {DateToStoryblokQueryableDateString} from "../helpers/dateHelper";
import {getArticlesByAuthorName} from "../api/articles/requests";
import {getArticlesByCategory} from "../api/category/requests";
import {useShowErrorFlashMessage} from "../features/flashMessage/store";
import {STATUS_NOT_FOUND} from "../constants/httpStatusCodes";

function useStoryblokArticles(filter, perPage, page = 1, story_language) {
  const [currentArticles, setCurrentArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [cachedPages, setCachedPages] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(""); // invalidate cache if this changes

  const mDate = moment.utc();
  const dateStr = DateToStoryblokQueryableDateString(mDate);

  useEffect(() => {
    let inputFilters = {filter_query: {}};
    let filterStr = "";
    if (filter) {
      for (const [key, value] of Object.entries(filter)) {
        if (key === "tag_list") {
          inputFilters.with_tag = value;
          filterStr += `tag_list=${value}`;
        } else if (key === "authors") {
          inputFilters.filter_query[key] = {any_in_array: value};
          filterStr += `${key}=${value}`;
        } else {
          inputFilters.filter_query[key] = {in: value};
          filterStr += `${key}=${value}`;
        }
      }
    }

    if (filterStr !== filters) {
      setCachedPages({});
      setFilters(filterStr);
    } else if (loading) {
      return;
    }
    if (cachedPages[page] && filterStr === filters) {
      setCurrentArticles(cachedPages[page]);
    } else {
      setLoading(true);
      setCurrentArticles([]);
      const storyblokApi = getStoryblokApi();
      const sBObj = {
        version: process.env.STORYBLOK_API_CONTENT_VERSION,
        content_type: "article",
        resolve_relations: 'article.authors',
        sort_by: "content.created_at:desc",
        page: page,
        per_page: perPage ? perPage : undefined,
        filter_query: {
          publish_on: {lt_date: dateStr},
          ...inputFilters.filter_query,
        },
      };
      if (story_language) {
        sBObj.starts_with = story_language;
      }
      if (inputFilters && inputFilters.with_tag) {
        sBObj.with_tag = inputFilters.with_tag;
      }
      storyblokApi
        .get("cdn/stories", sBObj)
        .then(({data, total}) => {
          if (data && data.stories && data.stories.length > 0) {
            const stories = FilterStoriesPastPublishOffDate(data.stories);
            const articles = stories.map(a => ArticleStoryToObject(a));
            setCurrentArticles(articles);
            let newCachedPages = {
              ...cachedPages,
            };
            newCachedPages[page] = articles;
            setCachedPages(newCachedPages);
            setTotal(total);
          }
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setLoading(false);
        });
    }
  }, [page, filter, perPage, cachedPages]);

  return [currentArticles, total, loading];
}

function useAuthorsArticles(authorName, perPage, page = 1) {
  const [currentArticles, setCurrentArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const showErrorFlashMessage = useShowErrorFlashMessage();

  useEffect(() => {
    setLoading(true);
    setCurrentArticles([]);

    getArticlesByAuthorName({name: authorName, page, pageSize: perPage})
      .then(({total, articles}) => {
        if (articles?.length > 0) {
          setCurrentArticles(articles);
          setTotal(total);
        }
      })
      .catch(err => {
        console.error(err);
        if (err.status !== STATUS_NOT_FOUND) {
          console.error(err);
          showErrorFlashMessage("api.network_error");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, authorName, perPage]);

  return [currentArticles, total, loading];
}

function useCategoryArticles(
  category,
  perPage,
  page = 1,
  additionalCategories = []
) {
  const [currentArticles, setCurrentArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const showErrorFlashMessage = useShowErrorFlashMessage();

  useEffect(() => {
    setCurrentArticles([]);

    getArticlesByCategory({
      category: [...additionalCategories, category],
      page,
      pageSize: perPage,
    })
      .then(({total, articles}) => {
        if (articles?.length > 0) {
          setCurrentArticles(articles);
          setTotal(total);
        }
      })
      .catch(err => {
        if (err.status !== STATUS_NOT_FOUND) {
          console.error(err);
          showErrorFlashMessage("api.network_error");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, category, perPage]);

  return [currentArticles, total, loading];
}

export {useStoryblokArticles, useAuthorsArticles, useCategoryArticles};
